import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, ArrowUpRightIcon } from 'lucide-react';
import { RESERVATION_TIMES, WINE_ADDON, WINE_FLIGHTS, type WineFlight } from '../../data/wine';
import { BOOKED_SEATS, CONTACT_EMAIL, SEATS_TOTAL, WINE_NIGHTS } from '../../data/bookingConfig';
import { upcomingWeekdayDates } from '../../utils/dates';
type Props = {
  selectedFlight: WineFlight['id'] | null;
  addonSelected: boolean;
  onSelectFlight: (id: WineFlight['id']) => void;
  onToggleAddon: () => void;
};
const labelClass = 'block mb-3 font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50';
const inputClass = 'w-full bg-white border border-ink/20 px-4 py-3 font-sans text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-ink transition-colors duration-200';
export function WineBooking({
  selectedFlight,
  addonSelected,
  onSelectFlight,
  onToggleAddon
}: Props) {
  const [nightName, setNightName] = useState<string>(WINE_NIGHTS[0].name);
  const [date, setDate] = useState<string>('');
  const [time, setTime] = useState<string>('');
  const [guests, setGuests] = useState(2);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const night = WINE_NIGHTS.find(item => item.name === nightName) ?? WINE_NIGHTS[0];
  const dateOptions = useMemo(() => upcomingWeekdayDates(night.index, 8), [night.index]);
  const selectedDate = dateOptions.some(option => option.value === date) ? date : '';
  const selectedOption = dateOptions.find(option => option.value === selectedDate);
  const flight = WINE_FLIGHTS.find(item => item.id === selectedFlight) ?? null;
  const perPerson = (flight?.price ?? 0) + (addonSelected && flight ? WINE_ADDON.price : 0);
  const total = perPerson * guests;
  const available = Math.max(0, SEATS_TOTAL - (BOOKED_SEATS[nightName] ?? 0));
  const isWaitlist = guests > available;
  const canSubmit = Boolean(flight && selectedDate && time && name && phone);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || !flight) return;
    const body = [isWaitlist ? 'VÄNTELISTA — BORDSBOKNING MED VINBRICKA' : 'BORDSBOKNING MED VINBRICKA', '', `Namn: ${name}`, `Telefon: ${phone}`, '', `Datum: ${nightName} ${selectedOption?.label ?? ''} (${selectedDate})`, `Tid: kl. ${time}`, `Antal personer: ${guests}`, '', `Vinbricka: ${flight.name.toUpperCase()}`, `  ${flight.count} · ${flight.servings}`, `  ${flight.priceLabel} per person`, ...(addonSelected ? [`Tillval: ${WINE_ADDON.name.toUpperCase()}`, `  ${WINE_ADDON.count} · ${WINE_ADDON.servings}`, `  ${WINE_ADDON.priceLabel} per person`] : []), '', `Total: ${total} kr (${perPerson} kr × ${guests})`, `Status: ${isWaitlist ? 'Väntelista' : 'Förfrågan om bord'}`, '', `Övrigt: ${notes || '—'}`, '', 'Skickat via lillaverket.se'].join('\n');
    const subject = `${isWaitlist ? 'Väntelista' : 'Bordsbokning'} — ${flight.name} — ${nightName} ${selectedDate} — ${guests} personer`;
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  return <section id="boka-bord" className="w-full px-6 py-24 md:py-32 border-t border-ink/10" aria-labelledby="bokabord-rubrik" data-id="element-104" data-mp="16n5tvm12md2m">
      <div className="max-w-6xl mx-auto" data-id="element-105" data-mp="xoaogc5up70d">
        <div className="max-w-2xl" data-id="element-106" data-mp="xploq2037wmq">
          <h2 id="bokabord-rubrik" className="font-sans text-5xl md:text-7xl font-medium tracking-[-0.03em] uppercase leading-[0.9] text-ink" data-id="element-107" data-mp="11p498wnzy5p1">
            Boka bord
          </h2>
          <p className="mt-6 font-serif italic text-2xl md:text-3xl font-light text-ink/80" data-id="element-108" data-mp="2w3x24nxhwida">
            Vinbrickan följer med bokningen.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-16 grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,22rem)] gap-10 lg:gap-16 items-start" data-id="element-109" data-mp="27aenpf5aaauw">
          {/* Steps */}
          <div className="space-y-12" data-id="element-110" data-mp="1xgo2qk8d8ijy">
            {/* Datum */}
            <fieldset data-id="element-111" data-mp="1sep0jdod3rol">
              <legend className={labelClass} data-id="element-112" data-mp="1sf8ekjaw5vxc">Datum</legend>
              <div className="flex gap-2" data-id="element-113" data-mp="1oe564hv6b265">
                {WINE_NIGHTS.map(item => <button key={item.name} type="button" onClick={() => {
                setNightName(item.name);
                setDate('');
              }} className={`flex-1 px-3 py-3 border font-sans text-[11px] tracking-[0.15em] uppercase transition-colors duration-200 ${nightName === item.name ? 'border-ink bg-ink text-white' : 'border-ink/20 text-ink/60 hover:border-ink/50 hover:text-ink'}`} data-id="element-114" data-mp="1jmwsf9mt8un6">
                    {item.name}
                  </button>)}
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-115" data-mp="3lddrs12lh6f2">
                {dateOptions.map(option => <button key={option.value} type="button" onClick={() => setDate(option.value)} aria-pressed={selectedDate === option.value} className={`px-3 py-3 border text-left transition-colors duration-200 ${selectedDate === option.value ? 'border-ink bg-ink text-white' : 'border-ink/20 text-ink/70 hover:border-ink/50 hover:text-ink'}`} data-id="element-116" data-mp="3qax774b13p8l">
                    <span className="block font-sans text-[13px]" data-id="element-117" data-mp="2evqkrlrgzqli">
                      {option.label}
                    </span>
                    {option.hint && <span className={`block mt-0.5 font-sans text-[10px] tracking-[0.12em] uppercase ${selectedDate === option.value ? 'text-white/65' : 'text-ink/40'}`} data-id="element-118" data-mp="26t7wo4nxaslb">
                        {option.hint}
                      </span>}
                  </button>)}
              </div>
            </fieldset>

            {/* Tid */}
            <fieldset data-id="element-119" data-mp="1xvfgkyadngwv">
              <legend className={labelClass} data-id="element-120" data-mp="1xvxmynqr7bii">Tid</legend>
              <div className="flex flex-wrap gap-2" data-id="element-121" data-mp="1svt2ynqmzynj">
                {RESERVATION_TIMES.map(slot => <button key={slot} type="button" onClick={() => setTime(slot)} aria-pressed={time === slot} className={`px-5 py-3 border font-sans text-sm tabular-nums transition-colors duration-200 ${time === slot ? 'border-ink bg-ink text-white' : 'border-ink/20 text-ink/70 hover:border-ink/50 hover:text-ink'}`} data-id="element-122" data-mp="399t0xt5x9e85">
                    {slot}
                  </button>)}
              </div>
            </fieldset>

            {/* Antal personer */}
            <fieldset data-id="element-123" data-mp="19tk7sq7fr9wc">
              <legend className={labelClass} data-id="element-124" data-mp="1dtb4tbnk2m9b">Antal personer</legend>
              <div className="flex items-center gap-4" data-id="element-125" data-mp="1duw2ha562lbe">
                <button type="button" onClick={() => setGuests(Math.max(1, guests - 1))} className="w-11 h-11 border border-ink/20 text-ink/60 hover:border-ink hover:text-ink transition-colors duration-200" aria-label="Minska antal personer" data-id="element-126" data-mp="100bt7kqw9c71">
                  −
                </button>
                <span className="w-12 text-center font-serif text-3xl font-light tabular-nums text-ink" data-id="element-127" data-mp="3tkf865gyufqc">
                  {guests}
                </span>
                <button type="button" onClick={() => setGuests(Math.min(40, guests + 1))} className="w-11 h-11 border border-ink/20 text-ink/60 hover:border-ink hover:text-ink transition-colors duration-200" aria-label="Öka antal personer" data-id="element-128" data-mp="3or3xft919et5">
                  +
                </button>
              </div>
              {isWaitlist && <p role="status" className="mt-4 px-4 py-3 border border-ink/40 bg-ink/[0.04] font-sans text-[11px] leading-relaxed text-ink/80" data-id="element-129" data-mp="6f7iaij26mtw">
                  Sällskapet är större än antalet platser vi kan hålla. Din
                  förfrågan skickas som väntelista — vi hör av oss så snart vi
                  kan.
                </p>}
            </fieldset>

            {/* Vinbricka */}
            <fieldset data-id="element-130" data-mp="12nn7hdww0drc">
              <legend className={labelClass} data-id="element-131" data-mp="16ndv58r4adrn">Vinbricka</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-132" data-mp="16nxcoocuv055">
                {WINE_FLIGHTS.map(item => <button key={item.id} type="button" onClick={() => onSelectFlight(item.id)} aria-pressed={selectedFlight === item.id} className={`px-5 py-4 border text-left transition-colors duration-200 ${selectedFlight === item.id ? 'border-ink bg-ink text-white' : 'border-ink/20 hover:border-ink/50'}`} data-id="element-133" data-mp="1bke35p7j1qle">
                    <span className="block font-sans text-sm font-medium uppercase tracking-[-0.01em]" data-id="element-134" data-mp="3w4cz8l9gj0vp">
                      {item.name}
                    </span>
                    <span className={`block mt-1 font-sans text-[11px] ${selectedFlight === item.id ? 'text-white/60' : 'text-ink/50'}`} data-id="element-135" data-mp="4yagwxn93znh">
                      {item.count} · {item.priceLabel}/person
                    </span>
                  </button>)}
              </div>

              <button type="button" onClick={onToggleAddon} disabled={!flight} aria-pressed={addonSelected} className={`mt-2 w-full px-5 py-4 border text-left transition-colors duration-200 ${addonSelected ? 'border-ink bg-ink text-white' : 'border-ink/20 hover:border-ink/50'} ${!flight ? 'opacity-40 cursor-not-allowed' : ''}`} data-id="element-136" data-mp="107gudi062wlw">
                <span className="block font-sans text-sm font-medium uppercase tracking-[-0.01em]" data-id="element-137" data-mp="1nwrvj0lfit0k">
                  {addonSelected ? '− ' : '+ '}
                  {WINE_ADDON.name}
                </span>
                <span className={`block mt-1 font-sans text-[11px] ${addonSelected ? 'text-white/60' : 'text-ink/50'}`} data-id="element-138" data-mp="34cz19z0p34ec">
                  {WINE_ADDON.count} · {WINE_ADDON.priceLabel}/person
                </span>
              </button>
              <p className="mt-2 font-sans text-[11px] leading-relaxed text-ink/45" data-id="element-139" data-mp="3s3cisvlnlkuo">
                Något Sjukt är ett tillval — det läggs till på Något Gott eller
                Något Extra och kan inte bokas på egen hand.
              </p>
            </fieldset>

            {/* Kontakt */}
            <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-140" data-mp="3deoostrghj6u">
              <legend className={labelClass} data-id="element-141" data-mp="eo6ftx4d54bk">Kontakt</legend>
              <div data-id="element-142" data-mp="epr9zlmrml8w">
                <label className="sr-only" htmlFor="vb-namn" data-id="element-143" data-mp="eq9hjeevolw4">
                  Namn
                </label>
                <input id="vb-namn" required value={name} onChange={e => setName(e.target.value)} className={inputClass} placeholder="För- och efternamn" data-id="element-144" data-mp="9vn2n19l8b7a" />
              </div>
              <div data-id="element-145" data-mp="1udo3lsga4ydu">
                <label className="sr-only" htmlFor="vb-tel" data-id="element-146" data-mp="1ze248pfjx61u">
                  Telefon
                </label>
                <input id="vb-tel" required type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} placeholder="07X XXX XX XX" data-id="element-147" data-mp="1kh1m9dn5wpgk" />
              </div>
              <div className="sm:col-span-2" data-id="element-148" data-mp="1gg9deli7cmkt">
                <label className="sr-only" htmlFor="vb-ovrigt" data-id="element-149" data-mp="35unuqkqw576a">
                  Övrigt
                </label>
                <textarea id="vb-ovrigt" rows={3} value={notes} onChange={e => setNotes(e.target.value)} className={inputClass} placeholder="Allergier, önskemål…" data-id="element-150" data-mp="3auabv59ub7kd" />
              </div>
            </fieldset>
          </div>

          {/* Summary */}
          <div className="lg:sticky lg:top-8" data-id="element-151" data-mp="3tru21y2o4lel">
            <motion.div layout className="border border-ink/20 p-7 md:p-8 bg-white" data-id="element-152" data-mp="3oy7u2j7g95p4">
              <h3 className="font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50" data-id="element-153" data-mp="7gy5i39s0470">
                Bokningsöversikt
              </h3>

              <dl className="mt-6 space-y-3" data-id="element-154" data-mp="gjza1ziztn8k">
                <SummaryRow label="Datum" value={selectedOption ? `${nightName} ${selectedOption.label}` : 'Välj datum'} muted={!selectedOption} data-id="element-155" data-mp="gixov289jkd4" />
                <SummaryRow label="Tid" value={time ? `kl. ${time}` : 'Välj tid'} muted={!time} data-id="element-156" data-mp="2eb1w4bc2la8c" />
                <SummaryRow label="Antal" value={`${guests} personer`} data-id="element-157" data-mp="2kofw5kcpdu63" />
              </dl>

              <div className="mt-7 pt-6 border-t border-ink/15" data-id="element-158" data-mp="2oqudu14mynvs">
                <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50" data-id="element-159" data-mp="2tq70olvibuyd">
                  Vinbricka
                </p>
                {flight ? <div className="mt-3 flex items-start justify-between gap-4" data-id="element-160" data-mp="1x1raqoohjjkq">
                    <div data-id="element-161" data-mp="212b8nugvur7x">
                      <p className="font-sans text-base font-medium uppercase tracking-[-0.01em] text-ink" data-id="element-162" data-mp="20zf8s0lh5sgu">
                        {flight.name}
                      </p>
                      <p className="mt-1 font-sans text-[11px] text-ink/50" data-id="element-163" data-mp="2as338oozfkce">
                        {flight.count} · {flight.servings}
                      </p>
                    </div>
                    <p className="font-serif text-xl font-light tabular-nums text-ink shrink-0" data-id="element-164" data-mp="1i9yvsdsls7jj">
                      {flight.priceLabel}
                      <span className="block font-sans text-[10px] tracking-[0.12em] uppercase text-ink/40 text-right" data-id="element-165" data-mp="1aperla8mqbfi">
                        /person
                      </span>
                    </p>
                  </div> : <p className="mt-3 font-sans text-sm text-ink/40" data-id="element-166" data-mp="wshqmfysrdkw">
                    Ingen vinbricka vald
                  </p>}
              </div>

              {addonSelected && flight && <div className="mt-6 pt-6 border-t border-ink/15" data-id="element-167" data-mp="n14zxroqitgg">
                  <div className="flex items-start justify-between gap-3" data-id="element-168" data-mp="i2ajuxi5jj01">
                    <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50" data-id="element-169" data-mp="i0h9b74f4emr">
                      Tillval
                    </p>
                    <button type="button" onClick={onToggleAddon} className="inline-flex items-center gap-1 font-sans text-[10px] tracking-[0.15em] uppercase text-ink/45 hover:text-ink transition-colors duration-200" data-id="element-170" data-mp="90u8amflpau8">
                      <XIcon strokeWidth={1.5} className="w-3 h-3" data-id="element-171" data-mp="3un7jqc3l6m4p" />
                      Ta bort
                    </button>
                  </div>
                  <div className="mt-3 flex items-start justify-between gap-4" data-id="element-172" data-mp="3llq40uu3cmgj">
                    <div data-id="element-173" data-mp="3lnktmmgb52qy">
                      <p className="font-sans text-base font-medium uppercase tracking-[-0.01em] text-ink" data-id="element-174" data-mp="d66eo6wjjo6r">
                        {WINE_ADDON.name}
                      </p>
                      <p className="mt-1 font-sans text-[11px] text-ink/50" data-id="element-175" data-mp="i6u5oskrs50z">
                        {WINE_ADDON.count} · {WINE_ADDON.servings}
                      </p>
                    </div>
                    <p className="font-serif text-xl font-light tabular-nums text-ink shrink-0" data-id="element-176" data-mp="8cv886vn59fd">
                      {WINE_ADDON.priceLabel}
                      <span className="block font-sans text-[10px] tracking-[0.12em] uppercase text-ink/40 text-right" data-id="element-177" data-mp="10vsdgt9041e8">
                        /person
                      </span>
                    </p>
                  </div>
                </div>}

              <div className="mt-7 pt-6 border-t border-ink/20 flex items-end justify-between gap-4" data-id="element-178" data-mp="2ue85z1j92zwq">
                <div data-id="element-179" data-mp="2qcmkirsnhdax">
                  <span className="block font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50" data-id="element-180" data-mp="2qc4jziyygcx7">
                    Total
                  </span>
                  {flight && <span className="block mt-1 font-sans text-[11px] text-ink/45 tabular-nums" data-id="element-181" data-mp="1xo8p8jsy4v32">
                      {perPerson} kr × {guests}
                    </span>}
                </div>
                <span className="font-serif text-4xl font-light tabular-nums text-ink" data-id="element-182" data-mp="26odtwm6r9fdj">
                  {total} kr
                </span>
              </div>

              <button type="submit" disabled={!canSubmit} className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-sans text-[11px] tracking-[0.2em] uppercase transition-colors duration-200 ${canSubmit ? 'bg-ink text-white hover:bg-ink/85' : 'bg-ink/10 text-ink/40 cursor-not-allowed'}`} data-id="element-183" data-mp="3n4q6pvwi4p6z">
                {isWaitlist ? 'Ställ mig på väntelista' : 'Boka bord'}
                {canSubmit && <ArrowUpRightIcon strokeWidth={1.5} className="w-3.5 h-3.5" data-id="element-184" data-mp="oqinypk3zm3h" />}
              </button>

              {!canSubmit && <p className="mt-3 font-sans text-[11px] leading-relaxed text-ink/45" data-id="element-185" data-mp="yoomcx6pia3l">
                  Välj vinbricka, datum, tid och fyll i dina kontaktuppgifter.
                </p>}

              <a href="#vin-pa-glas" className="mt-5 block text-center font-sans text-[10px] tracking-[0.2em] uppercase text-ink/45 hover:text-ink transition-colors duration-200" data-id="element-186" data-mp="30h2y1einqibk">
                Se vinlistan
              </a>
            </motion.div>
          </div>
        </form>
      </div>
    </section>;
}
function SummaryRow({
  label,
  value,
  muted = false
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return <div className="flex items-baseline justify-between gap-4" data-id="element-187" data-mp="2j7ili0yakego">
      <dt className="font-sans text-[11px] tracking-[0.15em] uppercase text-ink/45" data-id="element-188" data-mp="2j93e3l11pnbw">
        {label}
      </dt>
      <dd className={`font-sans text-sm text-right ${muted ? 'text-ink/40' : 'text-ink'}`} data-id="element-189" data-mp="3fv5fls7w7kqy">
        {value}
      </dd>
    </div>;
}